<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import { createNoneAgentWithTranslation } from 'dashboard/helper/agentHelper';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ContactDetailsItem from './ContactDetailsItem.vue';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';

const normalizeAgentOption = agent => {
  if (!agent) {
    return null;
  }

  return {
    ...agent,
    thumbnail: agent.thumbnail || agent.avatar_url || '',
  };
};

const dedupeAgentsById = agents => {
  const seenAgentIds = new Set();

  return agents.filter(agent => {
    if (!agent || seenAgentIds.has(agent.id)) {
      return false;
    }

    seenAgentIds.add(agent.id);
    return true;
  });
};

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
  },
  props: {
    contactId: {
      type: [Number, String],
      required: true,
    },
    // Quem abre o painel fora da conversa (lista, kanban, perfil de empresa)
    // pode ter o contato so no payload, sem registro na store. A store
    // continua mandando quando tem o registro.
    fallbackContact: {
      type: Object,
      default: null,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return {
      agentsList,
    };
  },
  data() {
    return {
      isUpdating: false,
    };
  },
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
      myTeams: 'teams/getMyTeams',
      teams: 'teams/getTeams',
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
    }),
    currentUserId() {
      return Number(this.currentUser.id);
    },
    isAdministrator() {
      return this.currentRole === 'administrator';
    },
    globalAgentsList() {
      return this.agents
        .filter(agent => agent.confirmed)
        .map(normalizeAgentOption)
        .sort((agentA, agentB) => agentA.name.localeCompare(agentB.name));
    },
    storedContact() {
      return this.$store.getters['contacts/getContact'](this.contactId);
    },
    contact() {
      const stored = this.storedContact;
      if (!this.fallbackContact) {
        return stored;
      }
      // A store manda quando o registro dela traz responsavel e setor. Quem
      // abre o painel pelo perfil da empresa encontra la um registro PARCIAL
      // (`{id}`, vindo de /contacts/<id>/companies) que, sendo truthy, escondia
      // o payload completo. `null` explicito e valor valido ("sem
      // responsavel"), por isso a checagem e por chave, nao por valor. Depois
      // de um `contacts/update` o registro ganha as chaves e a store volta a
      // mandar.
      return this.isStoredContactComplete ? stored : this.fallbackContact;
    },
    isStoredContactComplete() {
      const stored = this.storedContact;
      return (
        Boolean(stored?.id) && 'assignee_id' in stored && 'team_id' in stored
      );
    },
    currentAssignedAgentOption() {
      if (!this.contact?.assignee_id) {
        return null;
      }

      return normalizeAgentOption(
        this.contact?.assignee ||
          this.globalAgentsList.find(
            agent => agent.id === Number(this.contact.assignee_id)
          ) ||
          this.agentsList.find(
            agent => agent.id === Number(this.contact.assignee_id)
          )
      );
    },
    availableAgents() {
      // O responsavel do contato nao pertence a uma caixa de entrada: a lista tem
      // de ser a da conta inteira (a mesma da tela do contato) para quem pode
      // atribuir. `agentsList` so traz quem e atribuivel na caixa da conversa
      // aberta, e para quem nao e administrador isso escondia os demais
      // atendentes (ou deixava a lista vazia).
      const baseAgents = this.canEditContactAssignee
        ? this.globalAgentsList
        : this.agentsList.map(normalizeAgentOption);
      const mergedAgents = dedupeAgentsById([
        this.currentAssignedAgentOption,
        ...baseAgents,
      ]).filter(agent => agent.id !== 0);

      return [
        ...(this.currentAssignedAgentOption
          ? [createNoneAgentWithTranslation(key => this.$t(key))]
          : []),
        ...mergedAgents,
      ];
    },
    contactPermissions() {
      const user = this.currentUser;
      const accountId = this.$store.getters.getCurrentAccountId;
      return getUserPermissions(user, accountId);
    },
    canEditContactAssignee() {
      return (
        this.isAdministrator ||
        // `contact_assignment_manage` basta no servidor
        // (Contacts::AssignmentPermissionService) e nao implica `contact_edit`.
        hasPermissions(
          ['contact_edit', 'contact_assignment_manage'],
          this.contactPermissions
        )
      );
    },
    canManageContactTeam() {
      return (
        this.isAdministrator ||
        hasPermissions(
          ['contact_assignment_manage', 'contact_manage'],
          this.contactPermissions
        )
      );
    },
    assignedAgent: {
      get() {
        return this.currentAssignedAgentOption;
      },
      async set(agent) {
        const agentId = Number(agent?.id) > 0 ? agent.id : null;
        this.isUpdating = true;
        try {
          await this.$store.dispatch('contacts/update', {
            id: this.contactId,
            assignee_id: agentId,
          });
          useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
        } catch (error) {
          useAlert(
            error.response?.data?.error ||
              error.response?.data?.message ||
              this.$t('CONTACT_FORM.FORM.ASSIGNEE.HELP')
          );
        } finally {
          this.isUpdating = false;
        }
      },
    },
    normalizedContactTeamId() {
      return this.contact?.team_id ? Number(this.contact.team_id) : null;
    },
    currentTeamOption() {
      if (this.normalizedContactTeamId === null) return null;

      return (
        this.contact?.team ||
        this.teams.find(
          team => Number(team.id) === this.normalizedContactTeamId
        ) ||
        null
      );
    },
    selectableTeams() {
      if (!this.currentTeamOption) {
        return this.teams;
      }

      const noneOption = {
        id: 0,
        name: this.$t('TEAMS_SETTINGS.LIST.NONE'),
      };
      const hasCurrentTeam = this.teams.some(
        team => Number(team.id) === Number(this.currentTeamOption.id)
      );

      const allTeams = hasCurrentTeam
        ? this.teams
        : [this.currentTeamOption, ...this.teams];

      return [noneOption, ...allTeams];
    },
    isAssigneeDisabled() {
      return !this.canEditContactAssignee;
    },
    isTeamDisabled() {
      const oldTeamId = this.normalizedContactTeamId;
      if (!oldTeamId) return false;

      const isCurrentTeamMember = this.myTeams.some(
        team => Number(team.id) === oldTeamId
      );
      return !(this.canManageContactTeam || isCurrentTeamMember);
    },
    isAssignedToMyTeam() {
      return this.myTeams.some(
        team => Number(team.id) === this.normalizedContactTeamId
      );
    },
    primaryMyTeam() {
      return this.myTeams[0];
    },
    showSelfAssign() {
      if (!this.assignedAgent) return true;
      if (this.assignedAgent.id !== this.currentUserId) return true;
      return false;
    },
  },
  watch: {
    contactId() {
      this.hydrateStoredContact();
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('teams/get');
    this.hydrateStoredContact();
  },
  methods: {
    // Fora da conversa o contato pode estar na store so pela metade. Traz o
    // registro inteiro: o painel passa a ler a store e os vizinhos que a leem
    // (ex.: o nome nas "Conversas anteriores", que vinha como "(Sem nome)")
    // acertam junto. Ate a resposta chegar, `contact` mostra o fallback.
    hydrateStoredContact() {
      if (!this.fallbackContact) return;
      if (this.isStoredContactComplete && this.storedContact.name) return;
      this.$store.dispatch('contacts/show', { id: this.contactId });
    },
    onSelfAssign() {
      this.assignedAgent = this.currentUser;
    },
    async updateTeam(teamId) {
      this.isUpdating = true;
      try {
        await this.$store.dispatch('contacts/update', {
          id: this.contactId,
          team_id: teamId || null,
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('CONTACT_FORM.FORM.TEAM.HELP'));
      } finally {
        this.isUpdating = false;
      }
    },
    onClickAssignAgent(selectedItem) {
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },
    onClickAssignTeam(selectedItemTeam) {
      if (
        this.currentTeamOption &&
        Number(this.currentTeamOption.id) === Number(selectedItemTeam.id)
      ) {
        this.updateTeam(null);
      } else {
        this.updateTeam(selectedItemTeam.id);
      }
    },
    onAssignMyTeam() {
      if (!this.primaryMyTeam) return;

      this.updateTeam(this.primaryMyTeam.id);
    },
  },
};
</script>

<template>
  <div class="bg-transparent">
    <!-- Atribuição de Agente ao Contato -->
    <div class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONTACT_FORM.FORM.ASSIGNEE.LABEL')"
      >
        <template #button>
          <woot-button
            v-if="showSelfAssign && !isAssigneeDisabled"
            icon="arrow-right"
            variant="link"
            size="small"
            :disabled="isUpdating"
            @click="onSelfAssign"
          >
            {{ $t('CONVERSATION_SIDEBAR.SELF_ASSIGN') }}
          </woot-button>
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        class="panel-selector"
        :options="availableAgents"
        :selected-item="assignedAgent"
        :disabled="isAssigneeDisabled || isUpdating"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @click="onClickAssignAgent"
      />
    </div>

    <div class="multiselect-wrap--small mt-3">
      <ContactDetailsItem compact :title="$t('CONTACT_FORM.FORM.TEAM.LABEL')">
        <template #button>
          <woot-button
            v-if="primaryMyTeam && !isAssignedToMyTeam"
            icon="people-team"
            variant="link"
            size="small"
            :disabled="isUpdating"
            @click="onAssignMyTeam"
          >
            {{ $t('CONTACTS_PAGE.ASSIGN_SELECTED_CONTACTS_TO_TEAM') }}
          </woot-button>
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        class="panel-selector"
        :options="selectableTeams"
        :selected-item="currentTeamOption"
        :disabled="isTeamDisabled || isUpdating"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.INPUT')
        "
        @click="onClickAssignTeam"
      />
    </div>
  </div>
</template>
